import { SubmissionSuccessPage } from "../../../components/features/onboarding/shared/submission-success-page.tsx";
import { filterContactInput, contactErrors } from "../../../utils/contact-validation.ts";
import { useState, type FormEvent, type ReactNode } from "react";
import { Building2, CreditCard, ShieldCheck, CircleCheck } from "lucide-react";
import { Button, Checkbox, RadioGroup, TextField } from "../../../components/ui/index.ts";
import { AGENCY_FIELDS, AUTHORIZATION, BANK_FIELDS, BUSINESS_TYPES, INITIAL_VALUES, OTHER_BUSINESS_FIELD, OTHER_TERMS_FIELD, PAYMENT_METHODS, PAYMENT_TERMS, TAX_FIELDS, type AgencyField, type AgencyFieldKey } from "./agency-registration.config.ts";
import { useOnboardingSubmission } from "../../../components/features/onboarding/shared/use-onboarding-submission.ts";
import { mapAgency } from "../../../components/features/onboarding/shared/submission-mappers.ts";
import { OnboardingDocumentUploadField } from "../../../components/features/onboarding/shared/components/document-upload-field.tsx";
import { validateAgencyFile } from "../../../components/features/onboarding/consultant-agency-us/consultant-agency-us.config.ts";
import { W9_DOWNLOAD_URL } from "../../../components/features/onboarding/consultant-us/consultant-us.config.ts";
import styles from "./agency-registration.module.css";

function FormSection({ id, icon, title, description, children }: {
  id: string; icon: ReactNode; title: string; description: string; children: ReactNode;
}) {
  return (
    <section className={styles.section} aria-labelledby={id}>
      <header className={styles.sectionHeader}>
        <span className={styles.sectionIcon} aria-hidden>{icon}</span>
        <div><h2 id={id}>{title}</h2><p>{description}</p></div>
      </header>
      <div className={styles.sectionBody}>{children}</div>
    </section>
  );
}

export default function AgencyRegistrationPage() {
  const submission = useOnboardingSubmission("agency");
  const requiredFields: AgencyFieldKey[] = ["legalCompanyName", "primaryContact", "phone", "ein", "accountNumber", "bankName", "routingNumber"];
  const [tax, setTax] = useState<File | null>(null);
  const [values, setValues] = useState(() => ({ ...INITIAL_VALUES, email: submission.verified?.email ?? "" }));
  const [authorized, setAuthorized] = useState(false);
  const [reviewed, setReviewed] = useState(false);
  const [errors, setErrors] = useState<Partial<Record<AgencyFieldKey | "authorization" | "tax", string>>>({});

  const update = (key: AgencyFieldKey, value: string) => {
    value = filterContactInput(key, value);
    setValues((current) => ({ ...current, [key]: value }));
    setErrors((current) => ({ ...current, [key]: undefined }));
    setReviewed(false);
    setAuthorized(false);
  };
  const fields = (definitions: AgencyField[]) => definitions.map(({ key, label, placeholder, type, wide }) => (
    <div key={key} className={wide ? styles.fullWidth : undefined}>
      <TextField id={`agency-${key}`} label={label} required readOnly={key === "email" && Boolean(submission.verified)} placeholder={placeholder} type={type ?? "text"} maxLength={key === "businessAddress" ? 500 : 150} autoComplete="off" value={values[key]} error={errors[key]} onChange={(event) => update(key, event.target.value)} />
    </div>
  ));
  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (submission.busy || submission.complete) return;
    const next: typeof errors = contactErrors(values);
    for (const key of requiredFields) if (!values[key].trim()) next[key] = "This field is required.";
    if (!tax) next.tax = "Upload your completed W-9 or applicable W-8.";
    for (const key of ["email", "remittanceEmail"] as const) {
      if (values[key].trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(values[key].trim())) next[key] = "Enter a valid email address.";
    }
    if (values.businessType === "other" && !values.otherBusinessType.trim()) next.otherBusinessType = "Specify your business type.";
    if (values.paymentTerms === "other" && !values.otherPaymentTerms.trim()) next.otherPaymentTerms = "Specify your payment terms.";
    if (!authorized) next.authorization = "Please confirm the agency authorization.";
    setErrors(next);
    if (Object.keys(next).length) {
      setReviewed(false);
      document.getElementById(`agency-${Object.keys(next)[0]}`)?.focus();
      return;
    }
    if (await submission.submit(mapAgency(values), { tax: tax ?? undefined })) setReviewed(true);
  };

  if (submission.complete) return <SubmissionSuccessPage />;

  return (
    <div className={styles.page} data-theme="caelius-classic">
      <header className={styles.topBar}>
        <img src="/assets/images/logo.svg" width={150} alt="Caelius Consulting" />
        {/* <span className={styles.portalLabel}>Agency Registration</span> */}
      </header>
      <main className={styles.main}>

        <form onSubmit={submit} noValidate className={styles.form}>
          <fieldset disabled={submission.busy || submission.complete} className={styles.formFields}>
            <FormSection id="agency-information" icon={<Building2 size={23} />} title="Recruiting Agency Information" description="Provide your company details and primary contact information.
">
              <div className={styles.grid}>{fields(AGENCY_FIELDS)}</div>
              <div className={styles.divider} />
              <RadioGroup name="agency-business-type" label="Business Type" value={values.businessType} options={BUSINESS_TYPES} onChange={(value) => update("businessType", value)} />
              {values.businessType === "other" && <div className={styles.grid}>{fields([OTHER_BUSINESS_FIELD])}</div>}
              <div className={styles.grid}>{fields(TAX_FIELDS)}</div>
            </FormSection>
            <FormSection id="agency-payment" icon={<CreditCard size={23} />} title="Agency Payment Information" description="Tell us how your agency would like to receive payments.">
              <RadioGroup name="agency-payment-method" label="Payment Method" value={values.paymentMethod} options={PAYMENT_METHODS} onChange={(value) => update("paymentMethod", value)} />
              <div className={styles.grid}>{fields(BANK_FIELDS)}</div>
              <RadioGroup name="agency-payment-terms" label="Payment Terms" value={values.paymentTerms} options={PAYMENT_TERMS} onChange={(value) => update("paymentTerms", value)} />
              {values.paymentTerms === "other" && <div className={styles.grid}>{fields([OTHER_TERMS_FIELD])}</div>}
            </FormSection>
            <FormSection id="agency-documents" icon={<ShieldCheck size={23} />} title="Tax Documentation" description="Download, complete and upload your signed tax form.">
              <Button
                type="button"
                variant="accent"
                
                className={styles.downloadButton}
                onClick={() => window.open(W9_DOWNLOAD_URL, "_blank")}
              >
                Download W-9 Form
              </Button>
              <OnboardingDocumentUploadField id="agency-tax" label="Completed and signed W-9 / applicable W-8" required file={tax} error={errors.tax} validateFile={validateAgencyFile} onChange={(file) => { setTax(file); setErrors((current) => ({ ...current, tax: undefined })); }} />
            </FormSection>
            <FormSection id="agency-authorization-heading" icon={<ShieldCheck size={23} />} title="Agency Authorization *" description="Please confirm the information before submitting.">
              <Checkbox id="agency-authorization" label={AUTHORIZATION} checked={authorized} onChange={(checked) => { setAuthorized(checked); setReviewed(false); setErrors((current) => ({ ...current, authorization: undefined })); }} />
              {errors.authorization && <p className={styles.error} role="alert">{errors.authorization}</p>}
            </FormSection>
            <footer className={styles.footer}>
              <Button type="submit" variant="accent" disabled={reviewed || submission.busy || submission.complete}>{submission.busy ? "Submitting?" : submission.complete ? "Submitted" : "Submit"}</Button>
            </footer>
          </fieldset>
          {reviewed && <p role="status" className={styles.confirmation}><CircleCheck size={19} aria-hidden />Your agency registration was submitted successfully.</p>}
        </form>
      </main>
    </div>
  );
}
